import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/base/SEOHead';
import JsonLd from '../../components/base/JsonLd';
import Hero from './components/Hero';
import CompanyIntroduction from './components/CompanyIntroduction';
import Portfolio from './components/Portfolio';
import TireRFID from './components/TireRFID';
import PastExperience from './components/PastExperience';
import ValueCreation from './components/ValueCreation';
import WhyDiivy from './components/WhyDiivy';
import BrandStatement from './components/BrandStatement';
import FinalCTA from './components/FinalCTA';
import Footer from './components/Footer';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Diivy Medical Technology',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'Diivy is a holding company building focused ventures in health technology, safety, and commerce. Parent company of Diitect and Vetuvia, with an operating mindset and long-term approach to value creation.',
    foundingDate: '2020',
    sameAs: [],
    parentOrganization: {
      '@type': 'Organization',
      name: 'Diivy Medical Technology'
    },
    subOrganization: [
      {
        '@type': 'Organization',
        name: 'Diitect',
        description: 'Medical and safety technology for faster detection and response',
        url: `${siteUrl}/portfolio`
      },
      {
        '@type': 'Organization',
        name: 'Vetuvia',
        description: 'Veterinary health technology and commerce for modern care delivery',
        url: `${siteUrl}/portfolio`
      }
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Investor Relations',
      url: `${siteUrl}/contact`
    }
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Diivy',
    url: siteUrl,
    description: 'Diivy is a holding company building focused ventures in health technology, safety, and commerce.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <>
      <SEOHead
        title="Diivy | Holding Company for Health Technology, Safety & Commerce"
        description="Diivy is a holding company building focused ventures in health technology, safety, and commerce. Parent company of Diitect and Vetuvia, with an operating mindset and long-term approach to value creation."
        keywords="holding company, health technology, medical technology, safety technology, commerce platform, Diitect, Vetuvia, long-term value creation"
        canonical="/"
        ogTitle="Diivy | Focused Companies. Long-Term Value."
        ogDescription="Building focused companies in health technology, safety, and commerce with an operating mindset and long-term approach to growth."
        ogUrl="/"
        twitterTitle="Diivy | Focused Companies. Long-Term Value."
        twitterDescription="Building focused companies in health technology, safety, and commerce with an operating mindset and long-term approach to growth."
      />
      <JsonLd data={organizationSchema} />
      <JsonLd data={websiteSchema} />
      
      <div className="min-h-screen bg-[#F7F4EF]">
        {/* Navigation */}
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-white'
        }`}>
          <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C9A96E]/40 to-transparent"></div>
          
          <div className="max-w-[1400px] mx-auto px-12">
            <div className="flex items-center justify-between h-24">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/f30444846e2254d95b405efc135ce378/deea87c36f9c2a3377548eebefcbfbbb.png" 
                  alt="Diivy Medical Technology" 
                  className="h-11 w-auto object-contain"
                />
              </Link>

              {/* Center Navigation */}
              <div className="hidden md:flex items-center gap-14">
                <Link to="/about" className="text-[13px] font-medium text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap tracking-wide">
                  About
                </Link>
                <Link to="/diitect" className="text-[13px] font-medium text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap tracking-wide">
                  Diitect
                </Link>
                <Link to="/vetuvia" className="text-[13px] font-medium text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap tracking-wide">
                  Vetuvia
                </Link>
                <Link to="/world-cup" className="text-[13px] font-medium text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap tracking-wide">
                  World Cup
                </Link>
                <Link to="/tire-technology" className="text-[13px] font-medium text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap tracking-wide">
                  Tire Technology
                </Link>
                <Link to="/contact" className="text-[13px] font-medium text-gray-600 hover:text-[#C9A96E] transition-colors duration-300 whitespace-nowrap tracking-wide">
                  Contact
                </Link>
              </div>

              {/* Right CTA */}
              <Link 
                to="/contact"
                className="hidden md:block px-7 py-2.5 bg-transparent border border-[#C9A96E]/30 text-[13px] font-medium text-[#C9A96E] rounded-full hover:bg-[#C9A96E] hover:text-[#0A0F1E] hover:border-[#C9A96E] transition-all duration-300 whitespace-nowrap tracking-wide"
              >
                Investor Inquiry
              </Link>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <Hero />
        <CompanyIntroduction />
        <Portfolio />
        <TireRFID />
        <PastExperience />
        <ValueCreation />
        <WhyDiivy />
        <BrandStatement />
        <FinalCTA />
        <Footer />
      </div>
    </>
  );
}